import { useCurrency } from '@/lib/currency';
import { useEffect, useId, useRef, useState } from 'react';
import { chartColor, formatNumber, formatPercent } from '../dashboard-utils';
import type { DashboardChart } from '../types';

export const ChartEmpty = ({ rates = false }: { rates?: boolean }) => {
  useCurrency();
  return (
    <div className="dash-chart-empty">
      <svg viewBox="0 0 100 60" aria-hidden="true">
        <path d="M8 48H92M14 48V33M32 48V23M50 48V30M68 48V12M86 48V19" />
        <path d="M12 28L32 17L50 23L68 7L88 13" />
      </svg>
      <strong>{rates ? 'Aucun TJM renseigné' : 'Aucune donnée sur cette période'}</strong>
      <span>
        {rates
          ? 'Les montants apparaîtront dès qu’ils seront renseignés.'
          : 'Élargissez la période ou changez de responsable.'}
      </span>
    </div>
  );
};

export const LineChart = ({
  chart,
  variant = 'line',
}: {
  chart: DashboardChart;
  variant?: 'line' | 'area' | 'columns';
}) => {
  useCurrency();
  const id = useId();
  const [selected, setSelected] = useState<string[]>(() =>
    chart.series.slice(0, 4).map((series) => series.id),
  );
  const [point, setPoint] = useState<number | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const available = selected.filter((id) => chart.series.some((series) => series.id === id));
  const chosen = available.length ? available : chart.series.slice(0, 4).map((series) => series.id);
  const visible = chart.series.filter((series) => chosen.includes(series.id));
  const maximum = Math.max(1, ...visible.flatMap((series) => series.values));
  const scale = 10 ** Math.floor(Math.log10(maximum));
  const top = Math.ceil(maximum / scale) * scale;
  const height = 230,
    left = 48,
    right = 20,
    bottom = 30,
    plotHeight = height - bottom - 15;
  const band = (width - left - right) / Math.max(1, chart.labels.length);
  const columnWidth = (band * 0.72) / Math.max(1, visible.length);
  const x = (index: number) =>
    variant === 'columns'
      ? left + (index + 0.5) * band
      : left + (index / Math.max(1, chart.labels.length - 1)) * (width - left - right);
  const y = (value: number) => height - bottom - (value / top) * plotHeight;
  const active = Math.min(point ?? chart.labels.length - 1, chart.labels.length - 1);
  const hasData = chart.series.some((series) => series.values.some((value) => value > 0));
  useEffect(() => {
    if (!svg.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry && entry.contentRect.width > 0)
        setWidth(Math.max(320, Math.round(entry.contentRect.width)));
    });
    observer.observe(svg.current);
    return () => observer.disconnect();
  }, [hasData]);
  const tickStep = Math.max(
    1,
    Math.ceil(chart.labels.length / Math.max(3, Math.floor(width / 105))),
  );
  if (!hasData) return <ChartEmpty />;
  return (
    <div className="dash-line">
      <div className="dash-line__legend" aria-label="Séries du graphique">
        {chart.series.map((series, i) => (
          <button
            type="button"
            key={series.id}
            aria-pressed={chosen.includes(series.id)}
            aria-disabled={chosen.includes(series.id) && chosen.length === 1}
            onClick={() =>
              setSelected(
                chosen.includes(series.id)
                  ? chosen.length > 1
                    ? chosen.filter((value) => value !== series.id)
                    : chosen
                  : [...chosen, series.id],
              )
            }
          >
            <i style={{ background: chartColor(i) }} />
            {series.label}
          </button>
        ))}
      </div>
      <div className="dash-line__readout" aria-live="polite">
        <span>{chart.labels[active]}</span>
        {visible.map((series) => (
          <span key={series.id}>
            <i style={{ background: chartColor(chart.series.indexOf(series)) }} />
            {series.label} <strong>{formatNumber(series.values[active] ?? 0)}</strong>
          </span>
        ))}
      </div>
      <svg
        ref={svg}
        className="dash-line__svg"
        data-view={variant}
        viewBox={`0 0 ${width} ${height}`}
        role="slider"
        tabIndex={0}
        aria-label={`${chart.title} : période à explorer`}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, chart.labels.length - 1)}
        aria-valuenow={Math.max(0, active)}
        aria-valuetext={`${chart.labels[active] ?? ''}. ${visible.map((series) => `${series.label} : ${String(series.values[active] ?? 0)}`).join(', ')}`}
        onKeyDown={(event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          setPoint(
            event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? chart.labels.length - 1
                : Math.max(
                    0,
                    Math.min(
                      chart.labels.length - 1,
                      active + (event.key === 'ArrowLeft' ? -1 : 1),
                    ),
                  ),
          );
        }}
        onPointerMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const index =
            variant === 'columns'
              ? Math.floor((((event.clientX - rect.left) / rect.width) * width - left) / band)
              : Math.round(
                  ((((event.clientX - rect.left) / rect.width) * width - left) /
                    (width - left - right)) *
                    (chart.labels.length - 1),
                );
          setPoint(Math.max(0, Math.min(chart.labels.length - 1, index)));
        }}
        onPointerLeave={() => setPoint(null)}
      >
        <title>{chart.title}</title>
        <defs>
          {visible.map((series) => (
            <linearGradient id={`${id}-${series.id}`} key={series.id} x1="0" x2="0" y1="0" y2="1">
              <stop
                offset="0%"
                stopColor={chartColor(chart.series.indexOf(series))}
                stopOpacity="0.32"
              />
              <stop
                offset="100%"
                stopColor={chartColor(chart.series.indexOf(series))}
                stopOpacity="0.025"
              />
            </linearGradient>
          ))}
        </defs>
        {Array.from({ length: 5 }, (_, index) => {
          const value = (top * index) / 4;
          return (
            <g key={index}>
              <line
                x1={left}
                x2={width - right}
                y1={y(value)}
                y2={y(value)}
                className="dash-gridline"
              />
              <text x={left - 12} y={y(value) + 4} textAnchor="end">
                {new Intl.NumberFormat('fr-FR', {
                  notation: 'compact',
                  maximumFractionDigits: 1,
                }).format(value)}
              </text>
            </g>
          );
        })}
        {visible.map((series, seriesIndex) => {
          const points = series.values
            .map((value, index) => `${String(x(index))},${String(y(value))}`)
            .join(' ');
          const color = chartColor(chart.series.indexOf(series));
          return (
            <g key={series.id}>
              {variant === 'columns' ? (
                series.values.map((value, index) => (
                  <rect
                    key={index}
                    x={x(index) - band * 0.36 + seriesIndex * columnWidth}
                    y={y(value)}
                    width={columnWidth * 0.88}
                    height={y(0) - y(value)}
                    rx={Math.min(3, columnWidth * 0.2)}
                    fill={color}
                    opacity={point === null || active === index ? 0.9 : 0.45}
                  >
                    <title>{`${chart.labels[index]} · ${series.label} : ${formatNumber(value)}`}</title>
                  </rect>
                ))
              ) : (
                <>
                  {variant === 'area' && (
                    <polygon
                      points={`${String(x(0))},${String(y(0))} ${points} ${String(x(chart.labels.length - 1))},${String(y(0))}`}
                      fill={`url(#${id}-${series.id})`}
                    />
                  )}
                  <polyline
                    points={points}
                    stroke={color}
                    fill="none"
                    strokeWidth="2.8"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  {series.values.map((value, index) => (
                    <circle
                      key={index}
                      cx={x(index)}
                      cy={y(value)}
                      r={chart.labels.length <= 24 ? 3.2 : 1.8}
                      fill="white"
                      stroke={color}
                      strokeWidth="1.7"
                    >
                      <title>{`${chart.labels[index]} · ${series.label} : ${formatNumber(value)}`}</title>
                    </circle>
                  ))}
                </>
              )}
            </g>
          );
        })}
        {chart.labels.map((label, index) =>
          (index % tickStep === 0 && index < chart.labels.length - tickStep) ||
          index === chart.labels.length - 1 ? (
            <text
              key={index}
              x={x(index)}
              y={height - 5}
              textAnchor={
                index === 0 ? 'start' : index === chart.labels.length - 1 ? 'end' : 'middle'
              }
            >
              {label}
            </text>
          ) : null,
        )}
        <line
          x1={x(active)}
          x2={x(active)}
          y1={10}
          y2={height - bottom}
          stroke="#b7a8d6"
          strokeDasharray="4 5"
        />
        {variant !== 'columns' &&
          visible.map((series) => (
            <circle
              key={series.id}
              cx={x(active)}
              cy={y(series.values[active] ?? 0)}
              r="5"
              fill={chartColor(chart.series.indexOf(series))}
              stroke="white"
              strokeWidth="2.5"
            />
          ))}
      </svg>
      <span className="sr-only">
        Utilisez les flèches gauche et droite pour parcourir les périodes. Les valeurs sont aussi
        disponibles dans le tableau du graphique.
      </span>
    </div>
  );
};

export const DonutChart = ({ chart }: { chart: DashboardChart }) => {
  useCurrency();
  const [active, setActive] = useState<string | null>(null);
  const total = chart.rows.reduce((sum, row) => sum + row.value, 0);
  const selected = chart.rows.find((row) => row.id === active);
  return (
    <div className="dash-donut">
      <div className="dash-donut__visual">
        <svg
          viewBox="0 0 200 200"
          role="img"
          aria-label={`${chart.title} : ${formatNumber(total)} au total`}
        >
          <circle cx="100" cy="100" r="79" fill="none" stroke="#f0edf7" strokeWidth="18" />
          {chart.rows.map((row, index) => {
            const size = total ? (row.value / total) * 100 : 0;
            const start = total
              ? (chart.rows.slice(0, index).reduce((sum, item) => sum + item.value, 0) / total) *
                100
              : 0;
            return size ? (
              <circle
                key={row.id}
                cx="100"
                cy="100"
                r="79"
                fill="none"
                pathLength="100"
                stroke={chartColor(index)}
                strokeWidth={active === row.id ? 23 : 18}
                strokeDasharray={`${String(size - (chart.rows.filter((item) => item.value > 0).length > 1 ? Math.min(0.9, size / 5) : 0))} 100`}
                strokeDashoffset={-start}
                transform="rotate(-90 100 100)"
                opacity={active && active !== row.id ? 0.35 : 1}
                onPointerEnter={() => setActive(row.id)}
                onPointerLeave={() => setActive(null)}
              >
                <title>{`${row.label} : ${formatNumber(row.value)} (${formatPercent(total ? (row.value / total) * 100 : null)})`}</title>
              </circle>
            ) : null;
          })}
        </svg>
        <div className="dash-donut__center">
          <strong>{formatNumber(selected?.value ?? total)}</strong>
          <span>
            {selected ? formatPercent(total ? (selected.value / total) * 100 : null) : 'au total'}
          </span>
        </div>
      </div>
      <div className="dash-donut__legend" aria-label="Détail de la répartition">
        {chart.rows.length ? (
          chart.rows.map((row, index) => (
            <button
              key={row.id}
              type="button"
              onPointerEnter={() => setActive(row.id)}
              onPointerLeave={() => setActive(null)}
              onFocus={() => setActive(row.id)}
              onBlur={() => setActive(null)}
              onClick={() => setActive((current) => (current === row.id ? null : row.id))}
              className={row.value ? '' : 'is-zero'}
            >
              <i style={{ background: chartColor(index) }} />
              <span>{row.label}</span>
              <strong>{formatNumber(row.value)}</strong>
              <small>{formatPercent(total ? (row.value / total) * 100 : null)}</small>
            </button>
          ))
        ) : (
          <p>Aucune donnée sur cette période.</p>
        )}
      </div>
    </div>
  );
};

export const BarChart = ({ chart }: { chart: DashboardChart }) => {
  useCurrency();
  const [expanded, setExpanded] = useState(false);
  const maximum = Math.max(
    1,
    ...chart.rows.map((row) => (chart.kind === 'range' ? (row.max ?? row.value) : row.value)),
  );
  const total = chart.rows.reduce((sum, row) => sum + row.value, 0);
  const rows = expanded ? chart.rows : chart.rows.slice(0, 7);
  if (!chart.rows.length || !chart.rows.some((row) => row.value > 0))
    return <ChartEmpty rates={chart.unit === 'eur'} />;
  return (
    <div className="dash-bars">
      {chart.kind === 'range' && (
        <div className="dash-range-key">
          <span>Minimum</span>
          <span>
            <i />
            Moyenne
          </span>
          <span>Maximum</span>
        </div>
      )}
      {chart.kind === 'stacked' && (
        <div className="dash-line__legend">
          {chart.series.map((series, i) => (
            <span key={series.id}>
              <i style={{ background: chartColor(i) }} />
              {series.label}
            </span>
          ))}
        </div>
      )}
      <div className="dash-bars__rows">
        {rows.map((row, index) => (
          <div className="dash-bar-row" key={row.id}>
            <div className="dash-bar-row__label">
              <span>{row.label}</span>
              <strong>{formatNumber(row.value, chart.unit)}</strong>
              {chart.unit === 'number' && chart.kind !== 'stacked' && (
                <small>{formatPercent(total ? (row.value / total) * 100 : null)}</small>
              )}
            </div>
            <svg
              viewBox="0 0 600 22"
              preserveAspectRatio="none"
              role="img"
              aria-label={`${row.label} : ${formatNumber(row.value, chart.unit)}${chart.kind === 'range' ? ` ; minimum ${formatNumber(row.min ?? row.value, 'eur')} ; maximum ${formatNumber(row.max ?? row.value, 'eur')}` : ''}`}
            >
              <rect x="0" y="6" width="600" height="10" rx="5" fill="#f2f3f9" />
              {chart.kind === 'range' ? (
                <>
                  <rect
                    x={((row.min ?? row.value) / maximum) * 584 + 8}
                    y="5"
                    width={Math.max(
                      2,
                      (((row.max ?? row.value) - (row.min ?? row.value)) / maximum) * 584,
                    )}
                    height="12"
                    rx="6"
                    fill="#cbbce9"
                  />
                  <circle
                    cx={(row.value / maximum) * 584 + 8}
                    cy="11"
                    r="6"
                    fill="#7452ce"
                    stroke="white"
                    strokeWidth="2"
                  />
                </>
              ) : chart.kind === 'stacked' ? (
                (row.values ?? []).map((value, i, values) => (
                  <rect
                    key={i}
                    x={(values.slice(0, i).reduce((sum, v) => sum + v, 0) / maximum) * 600}
                    y="5"
                    width={(value / maximum) * 600}
                    height="12"
                    fill={chartColor(i)}
                  >
                    <title>{`${chart.series[i]?.label ?? ''} : ${formatNumber(value)}`}</title>
                  </rect>
                ))
              ) : (
                <rect
                  className="dash-bar-fill"
                  x="0"
                  y="6"
                  width={(row.value / maximum) * 600}
                  height="10"
                  rx="5"
                  fill={chartColor(index)}
                />
              )}
            </svg>
            {chart.kind === 'range' && (
              <div className="dash-range-values">
                <span>{formatNumber(row.min ?? row.value, 'eur')}</span>
                <span>{formatNumber(row.sample ?? 0)} TJM renseignés</span>
                <span>{formatNumber(row.max ?? row.value, 'eur')}</span>
              </div>
            )}
            {chart.kind === 'stacked' && (
              <span className="sr-only">
                {chart.series
                  .map((series, i) => `${series.label} : ${String(row.values?.[i] ?? 0)}`)
                  .join(', ')}
              </span>
            )}
          </div>
        ))}
      </div>
      {chart.rows.length > 7 && (
        <button
          className="dash-text-button"
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? 'Réduire la liste' : `Voir les ${String(chart.rows.length)} catégories`}
        </button>
      )}
    </div>
  );
};
